/**
 * The remix paid for at the Remix offer, started by itself (RemixOffer.jsx promises "your remix
 * starts as soon as you pay"). Mounted once for the whole app: the subscription comes back on its
 * success page and a pack on /ai-agent, and neither knows about clips.
 *
 * Once the payment is seen (utils/pendingRemix.js `paid`), the usage is asked every 2 s until the
 * account has a video to spend (Stripe tells the API in a few seconds), then the remix is made exactly
 * as the dialog would have made it and its page opens. After a minute and a half it says the payment
 * is slow and keeps the remix: the next page load tries again.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Loader2, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';
import { track } from '../../analytics/track';
import { useLocale } from '../../i18n/LocaleContext';
import { useUsageStore } from '../../stores/usageStore';
import { REMIX_PAID_EVENT, clearPendingRemix, readPendingRemix } from '../../utils/pendingRemix';
import { outOfVideos, remixEnabled, remixErrorMessage } from './presets';

const SLOW_MS = 90000;

export default function PendingRemix() {
  const { isAuthenticated } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  // null | 'waiting' | 'starting' | 'slow' | {failed: message}
  const [state, setState] = useState(() => (readPendingRemix()?.paid ? 'waiting' : null));
  const metrics = useUsageStore((store) => store.metrics);
  const fetchedAt = useUsageStore((store) => store.fetchedAt);
  const started = useRef(false);
  const since = useRef(Date.now());

  useEffect(() => {
    const paid = () => {
      since.current = Date.now();
      setState((now) => (now && now !== 'slow' ? now : 'waiting'));
    };
    window.addEventListener(REMIX_PAID_EVENT, paid);
    return () => window.removeEventListener(REMIX_PAID_EVENT, paid);
  }, []);

  // Asked again every 2 s while the payment is on its way, for a minute and a half at most
  useEffect(() => {
    if (state !== 'waiting' || !isAuthenticated) return undefined;
    useUsageStore.getState().fetchUsage();
    const ask = setInterval(() => useUsageStore.getState().fetchUsage(), 2000);
    const slow = setTimeout(() => {
      setState('slow');
      track('remix_autostart', { outcome: 'slow' });
    }, SLOW_MS);
    return () => { clearInterval(ask); clearTimeout(slow); };
  }, [state, isAuthenticated]);

  useEffect(() => {
    if (state !== 'waiting' || started.current || !metrics || !remixEnabled()) return;
    // Only a count asked since the payment was seen says whether the video is there
    if (!fetchedAt || fetchedAt < since.current) return;
    const kept = readPendingRemix();
    if (!kept) {
      setState(null);
      return;
    }
    if (outOfVideos(metrics)) return;
    started.current = true;
    setState('starting');
    api.createRemix(kept.request)
      .then(({ id }) => {
        clearPendingRemix();
        track('remix_autostart', { outcome: 'started', preset: kept.request.preset });
        setState(null);
        navigate(`/ai-agent/${id}`);
      })
      .catch((error) => {
        clearPendingRemix();
        track('remix_autostart', { outcome: 'failed', status: error?.response?.status });
        setState({ failed: remixErrorMessage(error, t) });
      });
  }, [state, metrics, fetchedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state || !isAuthenticated) return null;
  const failed = typeof state === 'object' ? state.failed : null;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(1rem+var(--consent-bar-h,0px))] z-[60] flex justify-center px-4">
      <div role="status" data-testid="pending-remix" className="pointer-events-auto flex max-w-md items-center gap-2.5 rounded-full border border-fuchsia-500/30 bg-background/95 py-2 pe-2 ps-3 text-sm text-foreground shadow-xl backdrop-blur">
        {state === 'slow' || failed
          ? <Crown className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
          : <Loader2 className="h-4 w-4 shrink-0 animate-spin text-fuchsia-500" aria-hidden />}
        <span className="min-w-0">{failed ? t('remixOffer.failed', { reason: failed }) : t(state === 'slow' ? 'remixOffer.slow' : 'remixOffer.starting')}</span>
        {(state === 'slow' || failed) && (
          <button type="button" onClick={() => setState(null)} aria-label={t('clips.remix.cancel')} className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}
